/**
 * WorkspaceSelector - global header workspace switcher
 *
 * - 0 workspaces → null (loading / no workspace state)
 * - 1 workspace  → static non-interactive label
 * - 2+ workspaces → dropdown switcher
 *
 * When the user picks a different workspace the preference is persisted via
 * POST /api/workspace/select and the page is reloaded so all context is
 * re-initialized for the new workspace.
 */

import type { WorkspaceSummary } from '@protoss/shared';
import { ChevronDownIcon, DropdownMenu, IconSize } from '@protoss/ui';

export interface WorkspaceSelectorProps {
  /** All workspaces the user belongs to */
  workspaces: WorkspaceSummary[];
  /** UUID of the currently active workspace */
  activeWorkspaceUuid: string | null;
  /** Called when the user picks a different workspace */
  onSelect: (workspaceUuid: string) => void;
}

/**
 * WorkspaceSelector
 *
 * Placed in the global header next to the wordmark.
 * - 0 workspaces  → null (loading or no membership)
 * - 1 workspace   → static label badge (no interaction)
 * - 2+ workspaces → dropdown switcher
 */
export function WorkspaceSelector({
  workspaces,
  activeWorkspaceUuid,
  onSelect,
}: WorkspaceSelectorProps) {
  if (workspaces.length === 0) {
    return null;
  }

  const active = workspaces.find((w) => w.uuid === activeWorkspaceUuid) ?? workspaces[0];

  if (workspaces.length === 1) {
    return (
      <div className="workspace-selector">
        <div className="workspace-selector__static" aria-label="Current workspace">
          <span className="workspace-selector__label">{active?.name ?? 'Workspace'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-selector">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="workspace-selector__trigger"
            aria-label="Switch workspace"
          >
            <span className="workspace-selector__label">{active?.name ?? 'Workspace'}</span>
            <ChevronDownIcon
              width={IconSize.sm}
              height={IconSize.sm}
              className="workspace-selector__caret"
              aria-hidden="true"
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="workspace-selector__content dropdown-content"
            sideOffset={4}
            align="start"
          >
            {workspaces.map((ws) => (
              <DropdownMenu.Item
                key={ws.uuid}
                className={`workspace-selector__item dropdown-item${ws.uuid === activeWorkspaceUuid ? ' workspace-selector__item--active' : ''}`}
                onSelect={() => {
                  if (ws.uuid !== activeWorkspaceUuid) {
                    onSelect(ws.uuid);
                  }
                }}
              >
                <span className="workspace-selector__item-label">{ws.name}</span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
